import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { message, Switch, Input, Select, Dropdown, Menu, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { uniq, uniqBy } from "lodash";
import { CREATE_TEAM, UPDATE_TEAM } from "../../../graphql/mutation";
import { GET_LOCATIONS, GET_ALL_USERS_TEAMS } from "../../../graphql/query";
import FilterDropdown from "../../../library/filterDropdown";
import FilterTextBox from "../../../library/filterTextbox";
import { closeDialog } from "../../../library/helpers";
import TextInput from "../../../library/textInput";
import { getLoaderStatus, setLoaderStatus } from "../../../redux/slices/loaderSlice";
import CustomButton from "../../commonComponents/button";
import CustomTable from "../../commonComponents/table";
import CustomTabs from "../../commonComponents/tabs";
import useLang from "../../../hooks/useLang";

const validateTeamInputs = team => {
  return team.name !== "" && team.leaderId !== "" && team.members && team.members.length;
};

const ModalTeamForm = props => {
  const {
    users,
    company,
    team: teamData,
    refreshTeams,
    handleLocatinFilter,
    handleCompanyRoleFilter,
    companyRolesFilter,
    handleFilterSelection,
    tabItem = "All",
    isSubmit,
    setIsSubmit,
    setVisible,
    onClose,
    setIsRemoveMembers,
    isRemoveMembers,
    isEditMode,
    setIsEditMode,
  } = props;
  const [lang] = useLang();
  const [usersData, setUsersData] = useState(users);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowKeysForMembers, setSelectedRowKeysForMembers] = useState([]);
  const [selectedRowKeysForAddMembers, setSelectedRowKeysForAddMembers] = useState([]);
  const [hoverRowKey, setHoverRowKey] = useState(null);

  const dispatch = useDispatch();

  const confirmLoader = useSelector(state => getLoaderStatus(state));

  const searchInputRef = useRef(null);

  const [isCheckAll, setIsCheckAll] = useState(false);
  const [activePane, setActivePane] = useState("1");

  const [variables, setVariables] = useState({
    companyId: parseInt(company.id),
    isLocationFilter: false,
    allUser: true,
  });

  const [locationList, setLocationList] = useState([]);
  const [team, setTeam] = useState({ companyId: company.id, members: [] });
  const [teamMembers, setTeamMembers] = useState([]);

  const [userVariables, setUserVariables] = useState({
    companyId: parseInt(company.id),
    isLocationFilter: false,
    allUser: true,
    search: "",
  });
  useEffect(() => {
    if (teamData) {
      const selectedUserResult = usersData?.filter(o1 =>
        teamData?.members?.some(o2 => +o1.id === +o2.memberId)
      );
      setTeam({ ...teamData });
      setTeamMembers(selectedUserResult);
    } else {
      setTeam({ companyId: company.id, members: [] });
    }
  }, [teamData, usersData]);

  useEffect(() => {
    if (selectedRowKeysForMembers?.length > 0) {
      setIsRemoveMembers(true);
    } else {
      setIsRemoveMembers(false);
    }
  }, [selectedRowKeysForMembers]);

  useEffect(() => {
    if (teamData) {
      const tempSelectedRowKeys = teamData?.members?.map(item => item?.memberId?.toString());
      setSelectedRowKeys(tempSelectedRowKeys);
      setSelectedRowKeysForAddMembers(tempSelectedRowKeys);
    }
  }, []);

  useEffect(() => {
    if (isSubmit) {
      // eslint-disable-next-line no-use-before-define
      submitTeam();
    }
  }, [isSubmit]);

  // eslint-disable-next-line no-unused-vars
  const { loading: teamLoader, refetch: refetchUsers } = useQuery(GET_ALL_USERS_TEAMS, {
    fetchPolicy: "network-only",
    variables: {
      ...userVariables,
    },
    onCompleted: res => {
      const selectedUserResult = res?.getUsers?.data?.filter(o1 =>
        teamData?.members?.some(o2 => +o1.id === +o2.memberId)
      );
      const unSelectedUserResult = res?.getUsers?.data?.filter(
        o1 => !teamData?.members?.some(o2 => +o1.id === +o2.memberId)
      );
      setUsersData(selectedUserResult.concat(unSelectedUserResult));
    },
  });

  // eslint-disable-next-line no-unused-vars
  const { data: resp } = useQuery(GET_LOCATIONS, {
    variables: { companyId: company?.id },
    onCompleted: res => {
      if (res?.getLocations?.length > 0) {
        const locationFilter = res.getLocations.map(location => {
          return {
            value: location.id,
            text: location.title,
          };
        });
        setLocationList([...locationFilter]);
      }
    },
  });

  const [createTeam] = useMutation(CREATE_TEAM, {
    onCompleted: () => {
      setIsSubmit(false);
      refreshTeams();
      onClose(false);
    },
  });

  const [updateTeam] = useMutation(UPDATE_TEAM, {
    onCompleted: () => {
      refreshTeams();
    },
  });

  const resetTeam = () => {
    setTeam({ companyId: company.id, members: [] });
  };

  const handleNameSearch = (searckKey, keyname) => {
    switch (keyname) {
      case "fullName":
        if (searckKey.length > 0) {
          setVariables({
            ...variables,
            search: searckKey,
          });
        } else {
          setVariables({ ...variables, search: "" });
        }
        break;
      default:
        break;
    }
  };

  const handleModelCloseDialog = () => {
    setVariables({
      companyId: parseInt(company.id),
      isLocationFilter: false,
      allUser: true,
      locationIds: [],
      companyRoles: [],
      search: "",
    });
    setIsCheckAll(prev => prev + 1);
    setActivePane("1");
    dispatch(setLoaderStatus({ open: false }));
    closeDialog("teamDialog");
  };

  const updateTeamField = (field, value) => {
    setTeam(prevState => {
      const newState = { ...prevState };
      newState[field] = value;
      // Improve this line
      if (field === "leaderId") {
        newState.leader = { id: value };
        const index = newState?.members?.findIndex(member => member.memberId === value);
        if (index === -1) {
          newState.members.push({ memberId: value });
        }
      }
      return newState;
    });
  };

  const submitTeam = async () => {
    // dispatch(setLoaderStatus({ open: true }));
    const toSaveTeam = { ...team, id: +team?.id };
    // Improve this line
    toSaveTeam.members.forEach(m => {
      // eslint-disable-next-line no-underscore-dangle
      delete m.__typename;
      delete m.member;
    });
    const { members } = toSaveTeam;
    let uniqMembers = uniq(
      members.map(item => +item.memberId),
      "memberId"
    ).map(item => {
      return { memberId: item };
    });
    if (teamData && !isRemoveMembers) {
      const temp = teamData?.members?.map(item => ({ memberId: item?.memberId }));
      uniqMembers = [...uniqMembers, ...temp];
    }
    if (validateTeamInputs(toSaveTeam)) {
      if (toSaveTeam.id) {
        await updateTeam({
          variables: {
            ...toSaveTeam,
            members: isRemoveMembers
              ? uniqMembers.filter(item => !selectedRowKeysForMembers.includes(item.memberId))
              : uniqMembers,
          },
        });
        setIsSubmit(false);
        onClose(false);
        setIsEditMode(false);
        handleModelCloseDialog();
        resetTeam();
      } else {
        await createTeam({ variables: { ...toSaveTeam, members: uniqMembers } });
        handleModelCloseDialog();
        resetTeam();
      }

      message.success(toSaveTeam.id ? lang.teamUpdated : lang.teamCreated);
    } else {
      message.error(lang.invalidFields);
    }
  };

  const handleActiveToggle = (checked, e, id) => {
    let currentMembers = team.members;
    if (checked) {
      // add to list
      currentMembers?.push({ memberId: id });
    } else {
      // remove from list
      currentMembers = currentMembers?.filter(u => +u.memberId !== +id);
    }

    setTeam(prevState => {
      const newState = { ...prevState };
      newState.members = currentMembers;
      return newState;
    });
  };

  const handleTeamLeadChange = data => {
    updateTeam({
      variables: {
        id: teamData?.id,
        leaderId: data?.id,
        members: teamData?.members?.map(item => ({ memberId: item?.memberId })),
        name: teamData?.name,
      },
    });
  };

  const handleRemoveTeamMember = data => {
    updateTeam({
      variables: {
        id: teamData?.id,
        leaderId: teamData?.leaderId,
        members: teamData?.members
          ?.filter(item => +item?.memberId !== +data?.id)
          ?.map(item => ({ memberId: item?.memberId })),
        name: teamData?.name,
      },
    });
  };

  const returnItems = record => {
    return (
      <Menu>
        <Menu.Item
          key="1"
          onClick={() => {
            handleTeamLeadChange(record);
          }}
        >
          <span>Make team lead</span>
        </Menu.Item>
        <Menu.Item
          key="2"
          onClick={() => {
            handleRemoveTeamMember(record);
          }}
        >
          <span>Remove from team</span>
        </Menu.Item>
      </Menu>
    );
  };

  const COLUMNS = [
    {
      title: lang.name,
      dataIndex: "fullName",
      key: "fullName",
      render: (value, record) => {
        return record?.fullName ? (
          <span>
            {record?.fullName}{" "}
            {+record?.id === +teamData?.leaderId ? <Tag color="#E2E8F0">Team lead</Tag> : ""}
          </span>
        ) : (
          <span>--</span>
        );
      },
      filterDropdown: props => (
        <FilterTextBox
          {...props}
          isCheckAll={isCheckAll}
          searchKeyword={variables?.search || ""}
          searchInputRef={searchInputRef}
          confirm={e => handleNameSearch(e, "fullName")}
        />
      ),
    },
    {
      title: lang.copmanyRole,
      render: (value, record) => {
        return record?.companyRole?.name ? (
          <span>{record?.companyRole?.name || "--"}</span>
        ) : (
          <span>--</span>
        );
      },
      filters: companyRolesFilter,
      filterDropdown: props => (
        <FilterDropdown
          {...props}
          confirm={e => handleCompanyRoleFilter(e)}
          dataIndex="companyRole"
          isCheckAll={isCheckAll}
        />
      ),
    },
    {
      title: lang.locations,
      render: (value, record) => {
        return record?.location?.title ? (
          <div className="flex justify-between items-center">
            <span>{record?.location?.title || "--"}</span>
            <div
              className={`flex items-center space-x-4 ${
                +record.id === hoverRowKey ? "visible" : "invisible"
              }`}
            >
              <Dropdown overlay={returnItems(record)} placement="bottom">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={17}
                  height={16}
                  fill="none"
                  className="ml-2 mt-1"
                >
                  <path fill="#E2E8F0" d="M.5 0h16v16H.5z" />
                  <path
                    stroke="#141414"
                    d="M8.833 4.666a.333.333 0 1 1-.667 0 .333.333 0 0 1 .667 0ZM8.833 8a.333.333 0 1 1-.667 0 .333.333 0 0 1 .667 0ZM8.833 11.333a.333.333 0 1 1-.667 0 .333.333 0 0 1 .667 0Z"
                  />
                </svg>
              </Dropdown>
            </div>
          </div>
        ) : (
          <span>--</span>
        );
      },
      filters: locationList,
      filterDropdown: props => (
        <FilterDropdown
          {...props}
          isCheckAll={isCheckAll}
          confirm={e => handleLocatinFilter(e)}
          dataIndex="Location"
        />
      ),
    },
  ];

  const onSelectChange = selectedKeys => {
    const tempMembers = selectedKeys.map(id => ({ memberId: id }));
    setTeam(prevState => {
      const newState = { ...prevState };
      newState.members = tempMembers;
      return newState;
    });
    setSelectedRowKeys(selectedKeys);
  };

  const onSelectChangeForMembers = selectedKeys => {
    setSelectedRowKeysForMembers(selectedKeys?.map(item => +item));
  };

  const onSelectChangeForAddMembers = selectedKeys => {
    const tempMembers = selectedKeys.map(id => ({ memberId: id }));
    setTeam(prevState => {
      const newState = { ...prevState };
      newState.members = tempMembers;
      return newState;
    });
    setSelectedRowKeysForAddMembers(selectedKeys?.map(item => +item));
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const rowSelectionForAddMembers = {
    selectedRowKeysForAddMembers,
    onChange: onSelectChangeForAddMembers,
  };

  const rowSelectionForMembers = {
    selectedRowKeysForMembers,
    onChange: onSelectChangeForMembers,
  };

  const TAB_ITEMS = [
    {
      label: lang.teamInfo,
      key: "1",
      children: (
        <>
          <div className="mb-2">{lang.teamName}</div>
          <TextInput
            placeholder={lang.enterValue}
            value={team.name}
            onChange={e => updateTeamField("name", e.target.value)}
          />
          <Select
            style={{ width: "100%", marginTop: 15 }}
            onChange={value => updateTeamField("leaderId", value)}
            placeholder="Select Leader"
            value={+team?.leader?.id || null}
          >
            {(usersData || []).map(item => (
              <Select.Option key={+item?.id} value={+item?.id}>
                {item.fullName}
              </Select.Option>
            ))}
          </Select>
          <CustomButton
            className="my-4"
            btnText="Next"
            type="primary"
            handleClick={() => setActivePane("2")}
          />
        </>
      ),
    },
    {
      label: lang.members,
      key: "2",
      children: (
        <>
          <div className="mb-2 w-[50%]">
            <Input
              placeholder="Search..."
              onChange={e => {
                refetchUsers({
                  ...userVariables,
                  search: e.target.value,
                }).then(res => {
                  const selectedUserResult = res?.data?.getUsers?.data?.filter(o1 =>
                    teamData?.members?.some(o2 => +o1.id === +o2.memberId)
                  );
                  const unSelectedUserResult = res?.data?.getUsers?.data?.filter(
                    o1 => !teamData?.members?.some(o2 => +o1.id === +o2.memberId)
                  );
                  setUsersData(selectedUserResult?.concat(unSelectedUserResult));
                });
              }}
              allowClear
            />
          </div>
          <div className="overflow-y-auto h-[454px] relative" id="antdTable">
            <CustomTable
              rowSelection={rowSelection}
              data={usersData}
              loading={{ spinning: teamLoader, tip: "" }}
              columns={COLUMNS}
              rowKey="id"
              pagination={false}
              getPopupContainer={() => document.getElementById("antdTable")}
            />
          </div>
        </>
      ),
    },
  ];

  const TAB_ITEMS_FOR_EDIT_NAME = [
    {
      label: lang.teamInfo,
      key: "1",
      children: (
        <>
          <div className="mb-2">{lang.teamName}</div>
          <TextInput
            placeholder={lang.enterValue}
            value={team?.name}
            onChange={e => updateTeamField("name", e.target.value)}
          />
        </>
      ),
    },
  ];

  const TAB_ITEMS_FOR_MEMBERS = [
    {
      label: lang.members,
      key: "1",
      children: (
        <>
          <div className="mb-2 w-[50%]">
            <Input
              placeholder="Search..."
              onChange={e => {
                refetchUsers({
                  ...userVariables,
                  search: e.target.value,
                }).then(res => {
                  const selectedUserResult = res?.data?.getUsers?.data?.filter(o1 =>
                    teamData?.members?.some(o2 => +o1.id === +o2.memberId)
                  );
                  const unSelectedUserResult = res?.data?.getUsers?.data?.filter(
                    o1 => !teamData?.members?.some(o2 => +o1.id === +o2.memberId)
                  );
                  setUsersData(selectedUserResult?.concat(unSelectedUserResult));
                });
              }}
              allowClear
            />
          </div>
          <div className="overflow-y-auto h-[454px] relative" id="antdTable">
            <CustomTable
              data={teamMembers}
              rowSelection={rowSelectionForMembers}
              loading={{ spinning: teamLoader, tip: "" }}
              columns={COLUMNS}
              rowKey="id"
              pagination={false}
              getPopupContainer={() => document.getElementById("antdTable")}
              onRow={record => ({
                onMouseEnter: () => {
                  setHoverRowKey(+record?.id);
                },
                onMouseLeave: () => {
                  setHoverRowKey(null);
                },
              })}
            />
          </div>
        </>
      ),
    },
    {
      label: "Add Members",
      key: "2",
      children: (
        <>
          <div className="mb-2 w-[50%]">
            <Input
              placeholder="Search..."
              onChange={e => {
                refetchUsers({
                  ...userVariables,
                  search: e.target.value,
                }).then(res => {
                  const selectedUserResult = res?.data?.getUsers?.data?.filter(o1 =>
                    teamData?.members?.some(o2 => +o1.id === +o2.memberId)
                  );
                  const unSelectedUserResult = res?.data?.getUsers?.data?.filter(
                    o1 => !teamData?.members?.some(o2 => +o1.id === +o2.memberId)
                  );
                  setUsersData(selectedUserResult?.concat(unSelectedUserResult));
                });
              }}
              allowClear
            />
          </div>
          <div className="overflow-y-auto h-[454px] relative" id="antdTable">
            <CustomTable
              // data={users}
              rowSelection={rowSelectionForAddMembers}
              data={usersData.filter(item => !selectedRowKeys.includes(item.id))}
              loading={{ spinning: teamLoader, tip: "" }}
              columns={COLUMNS}
              rowKey="id"
              pagination={false}
              getPopupContainer={() => document.getElementById("antdTable")}
            />
          </div>
        </>
      ),
    },
  ];

  return (
    <CustomTabs
      // tabItems={TAB_ITEMS}
      tabItems={
        tabItem === "All" ? TAB_ITEMS : isEditMode ? TAB_ITEMS_FOR_EDIT_NAME : TAB_ITEMS_FOR_MEMBERS
      }
      handleChange={key => setActivePane(key)}
      activeKey={activePane}
      defaultActiveKey="1"
    />
  );
};

export default ModalTeamForm;
